import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PillTabs, Panel, StatTile, money, money2, count as fmtCount } from "../components/lux";
import { InfoHint, RECOGNITION_HINTS } from "../components/InfoHint";
import { PnlStatement } from "../components/financials/PnlStatement";
import { RevenueProfitChart } from "../components/financials/RevenueProfitChart";
import { FinancialsTable } from "../components/financials/FinancialsTable";
import { DateIssuesNotice } from "../components/financials/DateIssuesNotice";
import { CurrencyDollar, TrendUp, Car, ChartBar, Users } from "@phosphor-icons/react";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "rentals", label: "Rentals" },
  { key: "chauffeur", label: "Chauffeur & Tours" },
  { key: "vehicles", label: "By Vehicle" },
  { key: "owners", label: "Owner Settlement" },
];

const day = (v) => (v ? String(v).slice(0, 10) : "—");
const pct = (v) => `${Number(v || 0).toFixed(1)}%`;

export default function Financials() {
  const [range, setRange] = useState({ from: "", to: "" });
  const [applied, setApplied] = useState({ from: "", to: "" });
  const [basis, setBasis] = useState("accrual");
  const [tab, setTab] = useState("overview");
  const [vehicleFilter, setVehicleFilter] = useState(null);

  const params = { basis };
  if (applied.from) params.from = applied.from;
  if (applied.to) params.to = applied.to;
  const key = [applied.from, applied.to, basis];

  const overview = useQuery({
    queryKey: ["fin-overview", ...key],
    queryFn: async () => (await api.get("/financials/overview", { params })).data,
  });
  const rentals = useQuery({
    queryKey: ["fin-rows", "rental", ...key],
    queryFn: async () => (await api.get("/financials/rows", { params: { ...params, segment: "rental" } })).data,
    enabled: tab === "rentals",
  });
  const chauffeur = useQuery({
    queryKey: ["fin-rows", "chauffeur", ...key],
    queryFn: async () => (await api.get("/financials/rows", { params: { ...params, segment: "chauffeur" } })).data,
    enabled: tab === "chauffeur",
  });
  const byVehicle = useQuery({
    queryKey: ["fin-vehicles", ...key],
    queryFn: async () => (await api.get("/financials/by-vehicle", { params })).data,
    enabled: tab === "vehicles",
  });
  const owners = useQuery({
    queryKey: ["fin-owners", ...key],
    queryFn: async () => (await api.get("/financials/owner-settlement", { params })).data,
    enabled: tab === "owners",
  });
  const summary = useQuery({
    queryKey: ["fin-summary", applied.from, applied.to],
    queryFn: async () => (await api.get("/financials/summary", { params: { from: applied.from || undefined, to: applied.to || undefined, segment: "all" } })).data,
  });

  const o = overview.data;
  const s = summary.data || {};
  const rentalRows = (rentals.data?.rows || []).filter((r) => !vehicleFilter || r.vehicle === vehicleFilter);

  const moneyCol = (key, label, tone) => ({ key, label, align: "right", fmt: (v) => money2(v), tone });

  return (
    <div className="space-y-8" data-testid="financials-page">
      <p className="text-sm text-muted-foreground -mt-2" data-testid="financials-header">
        Revenue is the full amount charged to the client. Owner cuts, affiliate payouts and trip costs come off it.
      </p>

      {/* Range + recognition basis */}
      <div className="flex flex-wrap items-end gap-3 border border-border bg-card p-4">
        <div>
          <div className="label-mono mb-1.5">From</div>
          <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="bg-input border-border w-44" data-testid="fin-from" />
        </div>
        <div>
          <div className="label-mono mb-1.5">To</div>
          <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="bg-input border-border w-44" data-testid="fin-to" />
        </div>
        <Button onClick={() => setApplied(range)} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="fin-apply">Apply</Button>
        <Button variant="outline" onClick={() => { setRange({ from: "", to: "" }); setApplied({ from: "", to: "" }); }} data-testid="fin-alltime">All time</Button>
        <div className="ml-auto flex items-center gap-2">
          <span className="label-mono">Basis</span>
          <div className="inline-flex border border-border overflow-hidden rounded-full">
            {[["accrual", "Accrual"], ["cash", "Cash"]].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setBasis(v)}
                className={`px-3.5 py-1.5 text-xs font-medium transition-colors ${basis === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}
                data-testid={`fin-basis-${v}`}
              >{l}</button>
            ))}
          </div>
          <InfoHint text={basis === "cash" ? RECOGNITION_HINTS.collected : RECOGNITION_HINTS.earned} testid="fin-basis-hint" />
        </div>
      </div>

      <DateIssuesNotice />

      {/* The three figures, never blended */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatTile testid="fin-earned" label={<span>Earned revenue <InfoHint text={RECOGNITION_HINTS.earned} /></span>} value={money(s.earned_revenue)} hint={`${fmtCount(s.bookings_count)} bookings · avg ${money(s.avg_booking_value)}`} icon={CurrencyDollar} tone="gold" gold />
        <StatTile testid="fin-booked" label={<span>Booked revenue <InfoHint text={RECOGNITION_HINTS.booked} /></span>} value={money(s.booked_revenue)} hint="Sold in this period" icon={TrendUp} tone="blue" />
        <StatTile testid="fin-collected" label={<span>Collected cash <InfoHint text={RECOGNITION_HINTS.collected} /></span>} value={money(s.collected_cash)} hint="Deposits + invoice payments" icon={ChartBar} tone="neutral" />
      </div>

      <PillTabs tabs={TABS} value={tab} onChange={setTab} testid="fin-tabs" />

      {tab === "overview" && (
        <div className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <PnlStatement d={o} basis={basis} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatTile testid="fin-card-bookings" label="Bookings in period" value={fmtCount(o?.cards?.bookings)} icon={Car} tone="neutral" />
              <StatTile testid="fin-card-avg-rev" label="Avg revenue / booking" value={money2(o?.cards?.avg_revenue)} icon={CurrencyDollar} tone="gold" />
              <StatTile testid="fin-card-avg-profit" label="Avg net profit / booking" value={o?.cards?.avg_net_profit === null ? "—" : money2(o?.cards?.avg_net_profit)} hint={o?.cards?.avg_net_profit === null ? "Accrual basis only" : undefined} icon={TrendUp} tone="green" />
            </div>
          </div>
          <Panel label="Trend" title="Revenue vs net profit by month" icon={ChartBar} testid="fin-chart-panel">
            <RevenueProfitChart monthly={o?.monthly} />
          </Panel>
        </div>
      )}

      {tab === "rentals" && (
        <div className="space-y-3">
          {vehicleFilter && (
            <div className="flex items-center gap-3 text-sm">
              <span className="label-mono">Filtered to</span>
              <b>{vehicleFilter}</b>
              <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setVehicleFilter(null)} data-testid="fin-clear-vehicle">Clear</Button>
            </div>
          )}
          <FinancialsTable
            testid="fin-rentals-table"
            exportName="rentals"
            loading={rentals.isLoading}
            rows={rentalRows}
            initialSort={{ key: "date", dir: "desc" }}
            columns={[
              { key: "date", label: "Date", fmt: day },
              { key: "customer", label: "Customer" },
              { key: "vehicle", label: "Vehicle", tone: "muted" },
              { key: "salesperson", label: "Salesperson" },
              { key: "affiliate", label: "Affiliate", fmt: (v, r) => (r.affiliate_payout ? (
                <div>
                  <div>{v || "Affiliate"}</div>
                  <div className="text-xs text-primary tnum">{money2(r.affiliate_payout)}</div>
                </div>
              ) : (v || "—")) },
              moneyCol("revenue", "Revenue", "gold"),
              moneyCol("direct_costs", "Direct costs"),
              moneyCol("owner_cut", "Owner cut"),
              moneyCol("net_profit", "Net profit"),
              { key: "status", label: "Status", fmt: (v) => <span className="label-mono">{v}</span> },
            ]}
          />
        </div>
      )}

      {tab === "chauffeur" && (
        <FinancialsTable
          testid="fin-chauffeur-table"
          exportName="chauffeur-tours"
          loading={chauffeur.isLoading}
          rows={chauffeur.data?.rows || []}
          initialSort={{ key: "date", dir: "desc" }}
          columns={[
            { key: "date", label: "Date", fmt: day },
            { key: "customer", label: "Customer" },
            { key: "vehicle", label: "Vehicle", tone: "muted" },
            { key: "salesperson", label: "Salesperson" },
            { key: "driver", label: "Driver" },
            moneyCol("driver_cost", "Driver cost"),
            moneyCol("revenue", "Revenue", "gold"),
            moneyCol("direct_costs", "Direct costs"),
            moneyCol("owner_cut", "Owner cut"),
            moneyCol("net_profit", "Net profit"),
            { key: "status", label: "Status", fmt: (v) => <span className="label-mono">{v}</span> },
          ]}
        />
      )}

      {tab === "vehicles" && (
        <FinancialsTable
          testid="fin-vehicles-table"
          exportName="by-vehicle"
          loading={byVehicle.isLoading}
          rows={byVehicle.data?.rows || []}
          initialSort={{ key: "net_profit", dir: "desc" }}
          onRowClick={(r) => { setVehicleFilter(r.vehicle); setTab("rentals"); }}
          columns={[
            { key: "vehicle", label: "Vehicle" },
            { key: "plate", label: "Plate", tone: "muted" },
            { key: "ownership", label: "Ownership", tone: "muted" },
            { key: "trips", label: "Trips", align: "right" },
            { key: "days_rented", label: "Days rented", align: "right" },
            { key: "utilization", label: "Utilization", align: "right", fmt: pct },
            moneyCol("revenue", "Revenue", "gold"),
            moneyCol("direct_costs", "Direct costs"),
            moneyCol("owner_cut", "Owner cut"),
            moneyCol("net_profit", "Net profit"),
            moneyCol("revenue_per_day", "Revenue / day"),
          ]}
        />
      )}

      {tab === "owners" && (
        <div className="space-y-5">
          <FinancialsTable
            testid="fin-owners-table"
            exportName="owner-settlement"
            loading={owners.isLoading}
            rows={owners.data?.rows || []}
            initialSort={{ key: "outstanding", dir: "desc" }}
            empty="No 3rd-party owner trips in this range."
            columns={[
              { key: "owner", label: "Owner" },
              { key: "vehicles", label: "Vehicles", align: "right" },
              { key: "trips", label: "Trips", align: "right" },
              moneyCol("gross", "Gross"),
              moneyCol("their_cut", "Their cut"),
              moneyCol("paid_out", "Paid out"),
              moneyCol("outstanding", "Outstanding", "gold"),
            ]}
          />
          <Panel label="Reconciliation" title="Against the 3rd-Party Owners tab" icon={Users} testid="fin-variance" flush>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div><div className="label-mono mb-1">Per-booking owner cuts (open)</div><div className="font-heading text-xl tnum">{money2(owners.data?.variance?.our_open_total)}</div></div>
              <div><div className="label-mono mb-1">Owners tab balance</div><div className="font-heading text-xl tnum">{money2(owners.data?.variance?.owners_tab_total)}</div></div>
              <div>
                <div className="label-mono mb-1">Delta</div>
                <div className={`font-heading text-xl tnum ${Math.abs(owners.data?.variance?.delta || 0) > 0.5 ? "text-amber-400" : "text-emerald-400"}`} data-testid="fin-variance-delta">
                  {money2(owners.data?.variance?.delta)}
                </div>
              </div>
            </div>
            {(owners.data?.variance?.mismatched || []).length > 0 && (
              <div className="border-t border-border">
                <div className="px-5 py-3 label-mono text-amber-400">{owners.data.variance.mismatched.length} bookings disagree</div>
                <div className="max-h-72 overflow-y-auto divide-y divide-border/50">
                  {owners.data.variance.mismatched.map((m) => (
                    <a key={m.id} href={`/bookings/view/${m.id}`} className="flex items-center gap-3 px-5 py-2.5 text-sm hover:bg-white/[0.03]" data-testid={`fin-variance-row-${m.id}`}>
                      <span className="flex-1 min-w-0 truncate">{m.customer || "—"} <span className="text-muted-foreground">· {m.owner} · {day(m.date)}</span></span>
                      <span className="tnum text-xs text-muted-foreground">booking {money2(m.owner_cut)}</span>
                      <span className="tnum text-xs text-amber-400">tab {money2(m.owners_tab)}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </Panel>
        </div>
      )}
    </div>
  );
}
